import axios from "axios";
import { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import { statusToast } from "../utils/notify";

import API from "../config.js";
const G    = "#FFB800";
const G2   = "#FFD700";
const GD   = "#C8860A";
const C    = "#00C2FF";
const GRAD = `linear-gradient(135deg, ${G2}, ${G}, ${GD})`;

const TABS = [
  { id: "deposit",  label: "Deposit",  icon: "↑", color: "#00e87a" },
  { id: "withdraw", label: "Withdraw", icon: "↓", color: "#ff6b6b" },
];

export default function Wallet() {
  const [tab,        setTab]        = useState("deposit");
  const [amount,     setAmount]     = useState("");
  const [walletAddr, setWalletAddr] = useState("");
  const [coin,       setCoin]       = useState("USDT (TRC20)");
  const [status,     setStatusRaw]  = useState(null);
  // Wrap setStatus so every status update also fires a toast (additive — inline UI stays).
  const setStatus = (s) => { setStatusRaw(s); statusToast(s); };
  const [loading,    setLoading]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [balance,    setBalance]    = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);

  // Deposit state
  const [payInfo,      setPayInfo]      = useState(null);
  const [depositCoin,  setDepositCoin]  = useState("usdttrc20");
  const [addrCopied,   setAddrCopied]   = useState(false);
  const [payStatus,    setPayStatus]    = useState(null);
  const [checkingPay,  setCheckingPay]  = useState(false);
  const [copied,       setCopied]       = useState(false);
  const pollRef = useRef(null);

  // Withdraw OTP state
  const [wOtpStep,    setWOtpStep]    = useState(0); // 0=form, 1=otp
  const [wOtp,        setWOtp]        = useState("");
  const [wOtpResend,  setWOtpResend]  = useState(0);
  const [wPending,    setWPending]    = useState(null); // {amount, walletAddr, coin}

  const token   = localStorage.getItem("token");
  const headers = { Authorization: "Bearer " + token };

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/api/dashboard`, { headers }),
      axios.get(`${API}/api/transactions`, { headers }),
    ]).then(([d, t]) => {
      setBalance(d.data.balance);
      const txns = Array.isArray(t.data) ? t.data : t.data.transactions || [];
      setRecentTxns(txns.slice(0, 5));
    }).catch((err) => {
      if (err.response && err.response.status === 401) {
        localStorage.removeItem("token"); window.location.href = "/";
      }
    });
  };

  useEffect(() => {
    if (!token) { window.location.href = "/"; return; }
    fetchData();
  }, []);

  // parse amount safely — strip commas, spaces
  const parsedAmt = parseFloat(String(amount).replace(/,/g, "").trim()) || 0;
  const fee       = (parsedAmt * 0.05).toFixed(2);
  const received  = (parsedAmt - Number(fee)).toFixed(2);

  const reset = () => { setStatus(null); setResult(null); };

  // Auto poll when payInfo is set
  useEffect(() => {
    if (!payInfo?.paymentId) { clearInterval(pollRef.current); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/api/payment-status/${payInfo.paymentId}`, { headers });
        const st  = res.data.status;
        setPayStatus(st);
        if (st === "finished" || st === "confirmed") {
          clearInterval(pollRef.current);
          fetchData();
          setStatus({ type: "success", msg: "✅ Payment confirmed! Balance updated." });
          setTimeout(() => { setPayInfo(null); setAmount(""); setPayStatus(null); }, 3000);
        } else if (st === "expired") {
          clearInterval(pollRef.current);
          setStatus({ type: "error", msg: "❌ Payment expired. Please try again." });
          setPayInfo(null);
        }
      } catch {}
    };
    poll(); // check immediately
    pollRef.current = setInterval(poll, 15000); // then every 15s
    return () => clearInterval(pollRef.current);
  }, [payInfo]);

  const handleDeposit = async () => {
    if (!amount || parsedAmt <= 0 || isNaN(parsedAmt)) {
      return setStatus({ type: "error", msg: "Please enter a valid USDT amount." });
    }
    if (parsedAmt < 5) return setStatus({ type: "error", msg: "Minimum deposit is 5 USDT." });
    setLoading(true); reset(); setPayInfo(null); setPayStatus(null);
    try {
      const res = await axios.post(`${API}/api/create-payment`, { amount: parsedAmt, currency: depositCoin }, { headers });
      if (res.data.message) {
        setStatus({ type: "error", msg: res.data.message });
      } else {
        setPayInfo(res.data);
        setStatus({ type: "success", msg: "Payment address generated! Send exact amount below." });
      }
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Payment creation failed. Please try again." });
    } finally { setLoading(false); }
  };

  const checkPaymentStatus = async () => {
    if (!payInfo?.paymentId) return;
    setCheckingPay(true);
    try {
      const res = await axios.get(`${API}/api/payment-status/${payInfo.paymentId}`, { headers });
      const st  = res.data.status || res.data.payment_status;
      setPayStatus(st);
      if (st === "completed" || st === "finished" || st === "confirmed") {
        fetchData();
        setStatus({ type: "success", msg: "Payment confirmed! Balance updated." });
        setTimeout(() => { setPayInfo(null); setAmount(""); }, 2000);
      }
    } catch (e) {
      setPayStatus("check_failed");
    } finally { setCheckingPay(false); }
  };

  const copyAddress = () => {
    if (!payInfo?.payAddress) return;
    navigator.clipboard.writeText(payInfo.payAddress);
    setAddrCopied(true);
    setTimeout(() => setAddrCopied(false), 2000);
  };

  const handleWithdrawConfirm = () => {
    if (!amount || parsedAmt <= 0 || isNaN(parsedAmt))
      return setStatus({ type: "error", msg: "Please enter a valid USDT amount." });
    if (!walletAddr.trim())
      return setStatus({ type: "error", msg: "Enter your wallet address." });
    if (balance !== null && parsedAmt > Number(balance))
      return setStatus({ type: "error", msg: `Insufficient balance. Available: ${fmtU(balance)}` });
    setWPending({ amount: parsedAmt, walletAddr: walletAddr.trim(), coin });
    setWOtpStep(1);
    setStatus(null);
  };

  const handleWithdrawSendOtp = async () => {
    setLoading(true); setStatus(null);
    try {
      const res = await axios.post(`${API}/api/withdraw/send-otp`,
        { amount: wPending.amount, walletAddress: wPending.walletAddr, coin: wPending.coin }, { headers });
      if (res.data.message?.toLowerCase().includes("invalid") || res.data.message?.toLowerCase().includes("insufficient")) {
        setStatus({ type: "error", msg: res.data.message });
      } else {
        setWOtpStep(2);
        setWOtpResend(60);
        setStatus({ type: "success", msg: res.data.message });
      }
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Failed to send OTP." });
    } finally { setLoading(false); }
  };

  const handleWithdrawVerifyOtp = async () => {
    if (!wOtp || wOtp.length !== 6)
      return setStatus({ type: "error", msg: "Enter the 6-digit OTP." });
    setLoading(true); setStatus(null);
    try {
      const res = await axios.post(`${API}/api/withdraw`,
        { amount: wPending.amount, walletAddress: wPending.walletAddr, coin: wPending.coin, otp: wOtp }, { headers });
      if (res.data.message?.toLowerCase().includes("invalid") || res.data.message?.toLowerCase().includes("expired") || res.data.message?.toLowerCase().includes("insufficient")) {
        setStatus({ type: "error", msg: res.data.message });
      } else {
        setResult({ fee: res.data.fee, received: res.data.received || res.data.finalAmount });
        setStatus({ type: "success", msg: "Withdrawal request submitted! Will be processed within 24 hours." });
        setAmount(""); setWalletAddr(""); setWOtp(""); setWOtpStep(0); setWPending(null);
        fetchData();
      }
    } catch (e) {
      setStatus({ type: "error", msg: e.response?.data?.message || "Withdrawal failed." });
    } finally { setLoading(false); }
  };

  // OTP resend countdown
  useEffect(() => {
    if (wOtpResend <= 0) return;
    const t = setTimeout(() => setWOtpResend(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [wOtpResend]);

  const handleSubmit = () => {
    if (tab === "deposit") handleDeposit();
    else if (wOtpStep === 0) handleWithdrawConfirm();
    else if (wOtpStep === 1) handleWithdrawSendOtp();
    else handleWithdrawVerifyOtp();
  };

  const activeTab = TABS.find(t => t.id === tab);

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.container} className="page-container">

        {/* Header */}
        <div style={s.header} className="page-header">
          <div>
            <h1 style={s.title}>Wallet</h1>
            <p style={s.sub}>Deposit & withdraw your USDT earnings</p>
          </div>
          {balance !== null && (
            <div style={s.balancePill}>
              <span style={s.balanceLabel}>Available</span>
              <span style={s.balanceVal}>{fmtU(balance)}</span>
            </div>
          )}
        </div>

        {/* ── Form (full width) ── */}
        <div style={s.card}>
            <div style={s.cardTopLine} />

            {/* Tab Bar */}
            <div style={s.tabBar}>
              {TABS.map(t => (
                <button key={t.id}
                  style={{
                    ...s.tabBtn,
                    ...(tab === t.id ? {
                      color: t.color,
                      borderBottom: `2px solid ${t.color}`,
                      background: t.color + "10",
                    } : {}),
                  }}
                  onClick={() => { setTab(t.id); reset(); setAmount(""); setWalletAddr(""); }}>
                  <span style={{ fontSize: 20 }}>{t.icon}</span> {t.label}
                </button>
              ))}
            </div>

            <div style={s.formBody}>
              {/* Alert */}
              {status && (
                <div style={{
                  ...s.alert,
                  background: status.type === "success" ? "rgba(0,232,122,0.07)" : "rgba(255,60,87,0.08)",
                  border:     status.type === "success" ? "1px solid rgba(0,232,122,0.25)" : "1px solid rgba(255,60,87,0.3)",
                  color:      status.type === "success" ? "#00e87a" : "#ff6b8a",
                }}>
                  <span style={{ fontSize: 16 }}>{status.type === "success" ? "✓" : "⚠"}</span>
                  {status.msg}
                </div>
              )}

              {/* Result */}
              {result?.received !== undefined && (
                <div style={s.resultBox}>
                  <ResultRow label="Fee (5%)"       val={`${Number(result.fee).toFixed(2)} USDT`} />
                  <div style={s.resultDivider} />
                  <ResultRow label="You Receive"
                    val={`${Number(result.received).toFixed(2)} USDT`}
                    valColor="#00e87a" bold />
                </div>
              )}

              {/* Amount + Network — hide when order is active */}
              {!(tab === "deposit" && payInfo) && !(tab === "withdraw" && wOtpStep >= 1) && (
                <>
                  <div style={s.fieldWrap}>
                    <label style={s.fieldLbl}>Amount (USDT)</label>
                    <div style={s.inputRow}>
                      <span style={s.inputPrefix}>USDT</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        placeholder="0.00"
                        value={amount}
                        onChange={e => { setAmount(e.target.value); setPayInfo(null); setPayStatus(null); }}
                        style={s.input}
                      />
                    </div>
                    {parsedAmt > 0 && (
                      <div style={s.amtHint}>
                        ≈ ${parsedAmt.toLocaleString("en-US", { minimumFractionDigits: 2 })} USD
                      </div>
                    )}
                  </div>

                  {tab === "deposit" && (
                    <div style={s.fieldWrap}>
                      <label style={s.fieldLbl}>Payment Network</label>
                      <select value={depositCoin} onChange={e => setDepositCoin(e.target.value)} style={s.select}>
                        <option value="usdttrc20">USDT — TRC20 (Tron) ⚡ Recommended</option>
                        <option value="usdtbep20">USDT — BEP20 (BSC)</option>
                        <option value="usdterc20">USDT — ERC20 (Ethereum)</option>
                        <option value="sol">USDT — SPL (Solana)</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              {/* PayInfo Panel — shown after deposit initiated */}
              {tab === "deposit" && payInfo && (
                <div style={s.payPanel}>
                  <div style={s.payPanelTop}>
                    <span style={s.payPanelTitle}>Send Crypto to this Address</span>
                    {payStatus && (
                      <span style={{
                        ...s.payStatusBadge,
                        background: ["finished","confirmed"].includes(payStatus) ? "rgba(0,232,122,0.12)" : "rgba(255,184,0,0.1)",
                        color:      ["finished","confirmed"].includes(payStatus) ? "#00e87a" : G,
                        borderColor:["finished","confirmed"].includes(payStatus) ? "rgba(0,232,122,0.3)" : "rgba(255,184,0,0.3)",
                      }}>
                        {payStatus === "finished" || payStatus === "confirmed" ? "✓ Confirmed" :
                         payStatus === "waiting"   ? "⏳ Waiting" :
                         payStatus === "expired"   ? "❌ Expired" :
                         payStatus === "confirming"? "🔄 Confirming" : payStatus}
                      </span>
                    )}
                  </div>

                  {/* QR Code */}
                  {payInfo.qrDataUrl && (
                    <div style={{ textAlign: "center", margin: "12px 0" }}>
                      <img src={payInfo.qrDataUrl} alt="QR Code"
                        style={{ width: 180, height: 180, borderRadius: 12, border: "2px solid rgba(255,184,0,0.3)", background: "#fff", padding: 8 }} />
                    </div>
                  )}

                  {/* Address */}
                  <div style={s.payRow}>
                    <span style={s.payLabel}>Address</span>
                    <span style={{ ...s.payVal, fontSize: 11, wordBreak: "break-all" }}>
                      {payInfo.payAddress}
                    </span>
                  </div>
                  <button
                    style={{
                      width: "100%", padding: "10px",
                      background: copied ? "rgba(0,232,122,0.12)" : "rgba(255,184,0,0.08)",
                      border: copied ? "1px solid rgba(0,232,122,0.4)" : "1px solid rgba(255,184,0,0.25)",
                      borderRadius: 8, cursor: "pointer",
                      color: copied ? "#00e87a" : G,
                      fontFamily: "'Figtree', sans-serif",
                      fontWeight: 700, fontSize: 13,
                      letterSpacing: 1, transition: "all 0.2s",
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(payInfo.payAddress);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}>
                    {copied ? "✓ Copied!" : "📋 Copy Address"}
                  </button>
                  <div style={s.payRow}>
                    <span style={s.payLabel}>Amount</span>
                    <span style={s.payVal}>{payInfo.payAmount} USDT</span>
                  </div>
                  <div style={s.payRow}>
                    <span style={s.payLabel}>Network</span>
                    <span style={s.payVal}>{payInfo.payCurrency}</span>
                  </div>
                  {payInfo.expiresAt && (
                    <div style={s.payRow}>
                      <span style={s.payLabel}>Expires</span>
                      <span style={{ ...s.payVal, color: "#ff9966" }}>
                        {new Date(payInfo.expiresAt).toLocaleTimeString()}
                      </span>
                    </div>
                  )}

                  <div style={s.payWarning}>
                    ⚠ Send <b>exact amount</b> on the correct network. Address expires in 30 minutes. Balance credited automatically after blockchain confirmation.
                  </div>

                  <div style={{ textAlign: "center", color: "rgba(160,180,220,0.45)", fontSize: 12, marginTop: 8 }}>
                    <span style={s.spinner} /> Auto-checking every 15 seconds...
                  </div>
                </div>
              )}

              {/* Withdraw-only fields */}
              {tab === "withdraw" && wOtpStep === 0 && (
                <>
                  <div style={s.fieldWrap}>
                    <label style={s.fieldLbl}>Wallet Address</label>
                    <div style={s.inputRow}>
                      <span style={s.inputPrefix}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8L2 7h20z"/></svg>
                      </span>
                      <input
                        type="text"
                        placeholder="Enter your crypto wallet address"
                        value={walletAddr}
                        onChange={e => setWalletAddr(e.target.value)}
                        style={{ ...s.input, fontFamily: "monospace", fontSize: 13 }}
                      />
                    </div>
                  </div>

                  <div style={s.fieldWrap}>
                    <label style={s.fieldLbl}>Network / Coin</label>
                    <select value={coin} onChange={e => setCoin(e.target.value)} style={s.select}>
                      {["USDT (TRC20)", "USDT (ERC20)", "USDT (BEP20)", "USDT (SOL)"].map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {parsedAmt > 0 && (
                    <div style={s.feeBox}>
                      <ResultRow label="Amount"     val={`${parsedAmt.toFixed(2)} USDT`} />
                      <ResultRow label="Fee (5%)"   val={`− ${fee} USDT`} valColor="#ff6b8a" />
                      <div style={{ borderTop: "1px solid rgba(255,184,0,0.1)", paddingTop: 8, marginTop: 4 }}>
                        <ResultRow label="You Receive" val={`${received} USDT`} valColor="#00e87a" bold />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Withdraw confirm step */}
              {tab === "withdraw" && wOtpStep === 1 && (
                <div style={s.otpBox}>
                  <div style={s.otpTitle}>⚠ Confirm Withdrawal</div>
                  <p style={s.otpSub}>Please verify your withdrawal details before proceeding.</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    <div style={s.confirmRow}>
                      <span style={s.confirmLbl}>Amount</span>
                      <span style={{ ...s.confirmVal, color: "#ff6b8a" }}>{wPending?.amount?.toFixed(2)} USDT</span>
                    </div>
                    <div style={s.confirmRow}>
                      <span style={s.confirmLbl}>Fee (5%)</span>
                      <span style={{ ...s.confirmVal, color: "#ffab00" }}>− {(wPending?.amount * 0.05)?.toFixed(2)} USDT</span>
                    </div>
                    <div style={{ ...s.confirmRow, borderTop: "1px solid rgba(255,184,0,0.1)", paddingTop: 10 }}>
                      <span style={s.confirmLbl}>You Receive</span>
                      <span style={{ ...s.confirmVal, color: "#00e87a", fontWeight: 700 }}>{(wPending?.amount * 0.95)?.toFixed(2)} USDT</span>
                    </div>
                    <div style={s.confirmRow}>
                      <span style={s.confirmLbl}>Network</span>
                      <span style={s.confirmVal}>{wPending?.coin}</span>
                    </div>
                    <div style={{ ...s.confirmRow, alignItems: "flex-start" }}>
                      <span style={s.confirmLbl}>Wallet Address</span>
                      <span style={{ ...s.confirmVal, wordBreak: "break-all", maxWidth: "60%", textAlign: "right", fontFamily: "monospace", fontSize: 12 }}>{wPending?.walletAddr}</span>
                    </div>
                  </div>
                  <button
                    style={{ background: "none", border: "none", color: "#ff6b8a", fontSize: 12, cursor: "pointer", padding: 0 }}
                    onClick={() => { setWOtpStep(0); setWPending(null); setStatus(null); }}>
                    ← Edit Details
                  </button>
                </div>
              )}

              {/* Withdraw OTP step */}
              {tab === "withdraw" && wOtpStep === 2 && (
                <div style={s.otpBox}>
                  <div style={s.otpTitle}>🔐 Verify Withdrawal</div>
                  <p style={s.otpSub}>An OTP has been sent to your registered email. Enter it below to confirm.</p>
                  <div style={s.inputRow}>
                    <span style={s.inputPrefix}>OTP</span>
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="Enter 6-digit OTP"
                      value={wOtp}
                      onChange={e => setWOtp(e.target.value.replace(/\D/g, ""))}
                      style={{ ...s.input, letterSpacing: 4, fontSize: 16 }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                    <button
                      style={{ background: "none", border: "none", color: wOtpResend > 0 ? "rgba(160,180,220,0.3)" : G, fontSize: 12, cursor: wOtpResend > 0 ? "not-allowed" : "pointer", padding: 0 }}
                      disabled={wOtpResend > 0}
                      onClick={() => { handleWithdrawSendOtp(); setWOtpResend(60); }}>
                      {wOtpResend > 0 ? `Resend OTP in ${wOtpResend}s` : "Resend OTP"}
                    </button>
                    <button
                      style={{ background: "none", border: "none", color: "#ff6b8a", fontSize: 12, cursor: "pointer", padding: 0 }}
                      onClick={() => { setWOtpStep(1); setWOtp(""); setStatus(null); }}>
                      ← Back
                    </button>
                  </div>
                </div>
              )}

              {/* Submit / Cancel */}
              {tab === "deposit" && payInfo ? (
                <button
                  style={{
                    ...s.submitBtn,
                    background: "rgba(255,60,87,0.1)",
                    border: "1px solid rgba(255,60,87,0.3)",
                    color: "#ff6b8a",
                    boxShadow: "none",
                    cursor: "pointer",
                  }}
                  onClick={() => { setPayInfo(null); setPayStatus(null); setAmount(""); setStatus(null); clearInterval(pollRef.current); }}>
                  ✕ Cancel Order
                </button>
              ) : (
                <button
                  style={{
                    ...s.submitBtn,
                    background: tab === "deposit"
                      ? GRAD
                      : "linear-gradient(135deg,#ff6b8a,#cc2244)",
                    boxShadow: tab === "deposit"
                      ? "0 4px 24px rgba(255,184,0,0.35)"
                      : "0 4px 20px rgba(255,60,87,0.3)",
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                  onClick={handleSubmit}
                  disabled={loading}>
                  {loading
                    ? <span style={s.spinner} />
                    : tab === "withdraw" && wOtpStep === 1
                      ? "✓ Confirm — Send OTP to Email"
                      : tab === "withdraw" && wOtpStep === 2
                      ? "✓ Verify & Submit Withdrawal"
                      : <><span style={{ fontSize: 20 }}>{activeTab.icon}</span> {activeTab.label} USDT</>}
                </button>
              )}
            </div>
          </div>

        {/* ── Recent Transactions (below form) ── */}
        <div style={s.recentWrap}>
          <div style={s.recentTopLine} />
          <div style={s.recentHeader}>
            <span style={s.recentTitle}>Recent Transactions</span>
            <a href="/transactions" style={s.recentViewAll}>View All →</a>
          </div>
          {recentTxns.length === 0 ? (
            <div style={s.recentEmpty}>No transactions yet.</div>
          ) : (
            <div>
              {recentTxns.map((t, i) => {
                const credit = ["deposit","interest","referral_bonus","level_income","manual_credit","invest_withdraw"].includes(t.type);
                const typeLabels = {
                  deposit:"Deposit", withdraw:"Withdraw",
                  interest:"Daily Interest", invest:"Invest", invest_withdraw:"Capital Withdraw",
                  referral_bonus:"Referral Bonus", level_income:"Level Income",
                  manual_credit:"Manual Credit", manual_debit:"Manual Debit",
                };
                const typeColors = {
                  deposit:"#00e87a", withdraw:"#ff6b6b",
                  interest:"#00e87a", invest:G, invest_withdraw:"#ff6b6b",
                  referral_bonus:G2, level_income:G,
                  manual_credit:"#00e87a", manual_debit:"#ff6b6b",
                };
                const color = typeColors[t.type] || G;
                return (
                  <div key={i} style={{...s.recentRow, borderBottom: i < recentTxns.length-1 ? "1px solid rgba(255,184,0,0.05)" : "none"}}>
                    <div style={{...s.recentIcon, color, background: color+"12", borderColor: color+"25"}}>
                      {credit ? "↑" : "↓"}
                    </div>
                    <div style={s.recentInfo}>
                      <span style={{...s.recentType, color}}>{typeLabels[t.type] || t.type}</span>
                      <span style={s.recentNote}>{t.note || "—"}</span>
                    </div>
                    <div style={s.recentRight}>
                      <span style={{...s.recentAmt, color: credit ? "#00e87a" : "#ff6b6b"}}>
                        {credit ? "+" : "−"}{(Number(t.amount)||0).toFixed(2)} USDT
                      </span>
                      <span style={s.recentDate}>
                        {t.date ? new Date(t.date).toLocaleDateString("en-US",{day:"2-digit",month:"short"}) : "—"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Info Row (3 columns) ── */}
        <div style={s.infoRow} className="info-row">
          <InfoCard icon="↑" title="Deposit Info" color="#00e87a" items={[
            "Deposits credited to your account instantly.",
            "Your referrer earns 10% bonus on your deposits.",
            "Multi-level income distributed across 10 levels.",
            "No minimum or maximum deposit limit.",
            "All amounts in USDT.",
          ]} />
          <InfoCard icon="↓" title="Withdrawal Info" color="#ff6b8a" items={[
            "5% processing fee on all withdrawals.",
            "Withdrawals processed within 24 hours.",
            "Ensure your wallet address is correct.",
            "Minimum withdrawal: 10 USDT.",
            "Supported: USDT TRC20 / ERC20 / BEP20.",
          ]} />
          <InfoCard icon="🔒" title="Security" color={C} items={[
            "All transactions are encrypted & logged on-chain.",
            "Only use your personal wallet addresses.",
            "Earnix never asks for your private key.",
            "Contact support on Telegram @earnix_global for suspicious activity.",
          ]} />
        </div>

      </div>
    </div>
  );
}

function ResultRow({ label, val, valColor, bold }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between",
      fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:13, marginBottom:6 }}>
      <span style={{ color:"rgba(160,180,220,0.6)" }}>{label}</span>
      <span style={{ color: valColor || "#e0e8ff", fontWeight: bold ? 700 : 400 }}>{val}</span>
    </div>
  );
}

function InfoCard({ icon, title, color, items }) {
  return (
    <div style={{ ...s.infoCard, borderColor: color + "22" }}>
      <div style={s.infoHead}>
        <span style={{ ...s.infoIconBox, color, background: color + "12", borderColor: color + "30" }}>{icon}</span>
        <span style={{ ...s.infoTitle, color }}>{title}</span>
      </div>
      <ul style={s.infoList}>
        {items.map((item, i) => (
          <li key={i} style={s.infoItem}>
            <span style={{ ...s.infoDot, background: color }} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

const fmtU = v =>
  "USDT " + (Number(v)||0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const s = {
  page: { minHeight:"100vh", background:"#060D1A" },
  container: { maxWidth:1100, margin:"0 auto", padding:"32px 24px" },
  header: {
    display:"flex", justifyContent:"space-between",
    alignItems:"center", marginBottom:28, flexWrap:"wrap", gap:16,
  },
  title: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:28,
    background:`linear-gradient(135deg,#f0e0a0,${G},${GD})`,
    WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
    letterSpacing:2, marginBottom:6,
  },
  sub: { fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:14, color:"rgba(160,180,220,0.5)" },
  balancePill: {
    background:"rgba(255,184,0,0.07)",
    border:"1px solid rgba(255,184,0,0.2)",
    borderRadius:12, padding:"12px 22px",
    textAlign:"center",
  },
  balanceLabel: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:10, color:"rgba(255,184,0,0.5)",
    letterSpacing:1.5, textTransform:"uppercase",
    display:"block", marginBottom:4,
  },
  balanceVal: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:18, color:G2,
    display:"block",
  },
  grid: {
    display:"grid",
    gridTemplateColumns:"1fr 300px",
    gap:20,
    alignItems:"start",
  },
  card: {
    background:"rgba(8,18,40,0.8)",
    border:"1px solid rgba(255,184,0,0.12)",
    borderRadius:16, overflow:"hidden",
    backdropFilter:"blur(20px)",
  },
  cardTopLine: {
    height:2,
    background:`linear-gradient(90deg,transparent,${G2},${G},${GD},transparent)`,
  },
  tabBar: {
    display:"flex",
    borderBottom:"1px solid rgba(255,184,0,0.08)",
    background:"rgba(0,0,0,0.2)",
  },
  tabBtn: {
    flex:1, display:"flex", alignItems:"center",
    justifyContent:"center", gap:8,
    padding:"16px 12px",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:600, fontSize:14,
    color:"rgba(160,180,220,0.4)",
    background:"none", border:"none",
    borderBottom:"2px solid transparent",
    cursor:"pointer", letterSpacing:0.5,
    transition:"all 0.2s",
  },
  formBody: { padding:"28px 30px", display:"flex", flexDirection:"column", gap:18 },
  fieldWrap: { display:"flex", flexDirection:"column", gap:6 },
  fieldLbl: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:600, fontSize:11,
    color:"rgba(255,184,0,0.6)",
    letterSpacing:1.2, textTransform:"uppercase",
  },
  inputRow: {
    display:"flex", alignItems:"center",
    background:"rgba(0,0,0,0.5)",
    border:"1px solid rgba(255,184,0,0.12)",
    borderRadius:8, overflow:"hidden",
  },
  inputPrefix: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:11,
    color:G, padding:"0 12px",
    letterSpacing:0.5, flexShrink:0,
    borderRight:"1px solid rgba(255,184,0,0.1)",
    height:"100%", display:"flex", alignItems:"center",
  },
  input: {
    flex:1, padding:"13px 14px",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:16, color:"#e0e8ff",
    background:"transparent",
    border:"none", outline:"none",
  },
  amtHint: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:11, color:"rgba(160,180,220,0.4)",
    marginTop:4,
  },
  select: {
    padding:"13px 14px",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:14, color:"#e0e8ff",
    background:"rgba(6,13,26,0.95)",
    border:"1px solid rgba(255,184,0,0.12)",
    borderRadius:8, outline:"none", cursor:"pointer", width:"100%",
  },
  feeBox: {
    background:"rgba(0,0,0,0.3)",
    border:"1px solid rgba(255,184,0,0.08)",
    borderRadius:8, padding:"14px 16px",
  },
  otpBox: {
    background:"rgba(0,194,255,0.04)",
    border:"1px solid rgba(0,194,255,0.15)",
    borderRadius:10, padding:"18px 16px",
  },
  otpTitle: {
    fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:15,
    color:"#00C2FF", marginBottom:6,
  },
  otpSub: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, color:"rgba(160,180,220,0.5)", margin:"0 0 14px",
  },
  confirmRow: {
    display:"flex", justifyContent:"space-between", alignItems:"center",
    padding:"6px 0",
  },
  confirmLbl: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, color:"rgba(160,180,220,0.45)",
  },
  confirmVal: {
    fontFamily:"'Figtree',sans-serif", fontWeight:600, fontSize:13, color:"#e0e8ff",
  },
  submitBtn: {
    width:"100%", padding:"15px",
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:13,
    color:"#060D1A", letterSpacing:1.5,
    border:"none", borderRadius:8,
    display:"flex", alignItems:"center",
    justifyContent:"center", gap:10,
    marginTop:4, transition:"all 0.2s",
  },
  spinner: {
    display:"inline-block", width:18, height:18,
    border:"2px solid rgba(6,13,26,0.3)",
    borderTop:"2px solid #060D1A",
    borderRadius:"50%",
    animation:"spin 0.7s linear infinite",
  },
  alert: {
    padding:"12px 16px", borderRadius:8,
    fontSize:13, fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    display:"flex", alignItems:"center", gap:10, lineHeight:1.5,
  },
  resultBox: {
    background:"rgba(255,184,0,0.04)",
    border:"1px solid rgba(255,184,0,0.15)",
    borderRadius:8, padding:"14px 16px",
  },
  resultDivider: { height:1, background:"rgba(255,184,0,0.1)", margin:"8px 0" },

  /* NowPayments panel */
  payPanel: {
    background: "rgba(0,194,255,0.04)",
    border: "1px solid rgba(0,194,255,0.2)",
    borderRadius: 10, padding: "18px",
    display: "flex", flexDirection: "column", gap: 10,
  },
  payPanelTop: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4,
  },
  payPanelTitle: {
    fontFamily: "'Figtree',sans-serif", fontWeight: 700, fontSize: 12,
    color: "#00C2FF", letterSpacing: 0.5,
  },
  payStatusBadge: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 700, fontSize: 10,
    border: "1px solid", borderRadius: 20, padding: "3px 10px", letterSpacing: 0.5,
  },
  payRow: {
    display: "flex", justifyContent: "space-between",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 13,
  },
  payLabel: { color: "rgba(160,180,220,0.5)" },
  payVal: { color: "#e0e8ff", fontWeight: 600 },
  payAddrLabel: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 11, color: "rgba(0,194,255,0.6)",
    textTransform: "uppercase", letterSpacing: 1, marginTop: 4,
  },
  payAddrBox: {
    display: "flex", alignItems: "center", gap: 8,
    background: "rgba(0,0,0,0.4)", border: "1px solid rgba(0,194,255,0.15)",
    borderRadius: 8, padding: "10px 12px",
  },
  payAddrText: {
    flex: 1, fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 11,
    color: "rgba(160,180,220,0.7)", wordBreak: "break-all",
  },
  copyAddrBtn: {
    flexShrink: 0, padding: "5px 12px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 11,
    border: "1px solid", borderRadius: 6, cursor: "pointer",
    transition: "all 0.2s",
  },
  payWarning: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize: 11, color: "#ffaa44",
    background: "rgba(255,170,68,0.06)", border: "1px solid rgba(255,170,68,0.2)",
    borderRadius: 6, padding: "8px 10px", lineHeight: 1.5,
  },
  checkStatusBtn: {
    width: "100%", padding: "11px",
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight: 600, fontSize: 12,
    color: "#00C2FF", background: "rgba(0,194,255,0.08)",
    border: "1px solid rgba(0,194,255,0.25)", borderRadius: 8,
    cursor: "pointer", display: "flex", alignItems: "center",
    justifyContent: "center", gap: 8, letterSpacing: 0.3,
  },

  /* Info row (3 columns) */
  infoRow: {
    display:"grid",
    gridTemplateColumns:"repeat(3,1fr)",
    gap:16,
    marginTop:16,
  },

  /* Info col */
  infoCol: { display:"flex", flexDirection:"column", gap:14 },
  infoCard: {
    background:"rgba(8,18,40,0.8)",
    border:"1px solid", borderRadius:12,
    padding:"18px 20px",
    backdropFilter:"blur(16px)",
  },
  infoHead: { display:"flex", alignItems:"center", gap:10, marginBottom:14 },
  infoIconBox: {
    width:32, height:32, borderRadius:6,
    border:"1px solid", display:"flex",
    alignItems:"center", justifyContent:"center", fontSize:15,
  },
  infoTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:600, fontSize:12, letterSpacing:1,
  },
  infoList: { listStyle:"none", display:"flex", flexDirection:"column", gap:7 },
  infoItem: {
    display:"flex", alignItems:"flex-start", gap:8,
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, color:"rgba(160,180,220,0.6)", lineHeight:1.5,
  },
  infoDot: {
    width:4, height:4, borderRadius:"50%",
    flexShrink:0, marginTop:5,
  },

  /* Recent Transactions */
  recentWrap: {
    background:"rgba(8,18,40,0.95)",
    border:"1px solid rgba(255,184,0,0.1)",
    borderRadius:14, overflow:"hidden",
    marginTop:24,
    boxShadow:"0 4px 30px rgba(0,0,0,0.4)",
  },
  recentTopLine: {
    height:2,
    background:`linear-gradient(90deg, transparent, ${G2}, ${G}, ${GD}, transparent)`,
  },
  recentHeader: {
    display:"flex", justifyContent:"space-between", alignItems:"center",
    padding:"16px 20px 12px",
    borderBottom:"1px solid rgba(255,184,0,0.07)",
  },
  recentTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:13, color:G2, letterSpacing:1,
  },
  recentViewAll: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, color:`rgba(255,184,0,0.55)`,
    textDecoration:"none", letterSpacing:0.3,
  },
  recentEmpty: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:13, color:"rgba(160,180,220,0.3)",
    textAlign:"center", padding:"32px",
  },
  recentRow: {
    display:"flex", alignItems:"center", gap:14,
    padding:"14px 20px",
  },
  recentIcon: {
    width:34, height:34, borderRadius:8,
    display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:15, fontWeight:700,
    border:"1px solid", flexShrink:0,
  },
  recentInfo: {
    flex:1, display:"flex", flexDirection:"column", gap:3,
  },
  recentType: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:600, fontSize:13,
  },
  recentNote: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:11, color:"rgba(160,180,220,0.4)",
  },
  recentRight: { textAlign:"right" },
  recentAmt: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:13,
    display:"block", marginBottom:3,
  },
  recentDate: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:11, color:"rgba(160,180,220,0.35)",
  },

  /* Security section */
  securityWrap: {
    background:"rgba(0,194,255,0.03)",
    border:"1px solid rgba(0,194,255,0.1)",
    borderRadius:14, overflow:"hidden",
    marginTop:16,
  },
  securityInner: {
    display:"flex", alignItems:"flex-start", gap:20,
    padding:"20px 24px",
  },
  securityIcon: {
    width:44, height:44, borderRadius:10,
    display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:22, border:"1px solid", flexShrink:0,
  },
  securityList: {
    display:"flex", flexDirection:"column", gap:8,
  },
  securityItem: {
    display:"flex", alignItems:"flex-start", gap:10,
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:13, color:"rgba(160,180,220,0.6)", lineHeight:1.5,
  },
};




